import { useEffect, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { adminDashboardComputedAt } from '../lib/admin-dashboard-freshness'
import { RefreshCcw } from 'lucide-react'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import {
  Banner,
  Button,
  Card,
  ErrorState,
  FitMoneyValue,
  MoneyValue,
  Skeleton,
  Toggle,
} from '@shared/design-system/ui'
import { paymentMethodLabel } from '@entities/sale'
import type { MetricRow } from '../api/admin.api'
import { useAdminDashboardOverview } from '../api/admin-dashboard-overview'
import { useAdminDashboardLiveSeries } from '../api/admin-dashboard-live-series'
import {
  DEFAULT_DASHBOARD_PERIOD,
  dashboardPeriodLabel,
  dashboardRangeLabel,
} from '../lib/admin-dashboard-period'
import { dashboardOperationalRates } from '../lib/admin-dashboard-rates'
import { AdminSalesChart } from './AdminSalesChart'
import { AdminDashboardFilters } from './AdminDashboardFilters'
import type { DashboardSelection } from './AdminDashboardFilters'
import * as styles from './admin-dashboard.css'
import {
  header,
  titleBlock,
  title,
  subtitle,
  page,
  panelTitle,
  table,
  tableCard,
  tableViewport,
  td,
  th,
  cardHeader,
} from './admin.css'

function MetricCard({
  label,
  rows,
  currency,
  description,
  countLabel = 'cobranças',
  rate,
  totalCount,
}: {
  label: string
  rows: MetricRow[]
  currency: string
  description: string
  countLabel?: string
  rate?: string
  totalCount?: number
}) {
  const values = rows.length ? rows : [{ currency, amount: { amount: '0', currency }, count: 0 }]
  return (
    <Card padding="md" className={styles.metric}>
      <h3 className={panelTitle}>{label}</h3>
      {values.map((row) => (
        <div key={row.currency} className={styles.stack}>
          <FitMoneyValue
            amount={row.amount.amount}
            currency={row.currency}
            maxSize={21}
            minSize={12}
            fractionSize="lg"
          />
          <span className={styles.description}>
            {row.count.toLocaleString('pt-BR')}{' '}
            {row.count === 1
              ? countLabel === 'cobranças'
                ? 'cobrança'
                : 'lançamento'
              : countLabel}{' '}
            com valor em {row.currency}
          </span>
          {(row.unconvertedCount ?? 0) > 0 ? (
            <span className={styles.partial}>
              Total parcial: {row.unconvertedCount}{' '}
              {row.unconvertedCount === 1
                ? 'operação sem conversão disponível não entra'
                : 'operações sem conversão disponível não entram'}{' '}
              neste valor.
            </span>
          ) : null}
        </div>
      ))}
      {totalCount !== undefined ? (
        <span className={styles.description}>
          {totalCount.toLocaleString('pt-BR')}{' '}
          {totalCount === 1 ? 'cobrança no recorte' : 'cobranças no recorte'}
        </span>
      ) : null}
      {rate ? (
        <span className={styles.rate}>
          {rate} <span className={styles.description}>no recorte operacional</span>
        </span>
      ) : null}
      <p className={styles.description}>{description}</p>
    </Card>
  )
}

export function AdminDashboardScreen() {
  const lightLiveViewEnabled = useFeatureFlag('adminDashboardLightLiveView')
  const [selection, setSelection] = useState<DashboardSelection>({
    period: DEFAULT_DASHBOARD_PERIOD,
  })
  const currency = 'BRL'
  const [live, setLive] = useState(false)
  const [liveStageOpen, setLiveStageOpen] = useState(
    () =>
      lightLiveViewEnabled &&
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get('live') === '1',
  )
  const liveStageOpenRef = useRef(liveStageOpen)
  const isToday = selection.period === 'today'
  useEffect(() => {
    if (
      !lightLiveViewEnabled ||
      !isToday ||
      liveStageOpenRef.current ||
      new URLSearchParams(window.location.search).get('live') !== '1'
    ) {
      return
    }
    liveStageOpenRef.current = true
    setLiveStageOpen(true)
  }, [isToday, lightLiveViewEnabled])
  const isLiveStageOpen = lightLiveViewEnabled && isToday && liveStageOpen
  const queryClient = useQueryClient()
  const params = {
    currency,
    ...(selection.user ? { participantId: selection.user.id } : {}),
    ...(selection.period === 'custom' ? selection.custom : {}),
  }
  const overviewHasLoaded =
    queryClient.getQueryData(['admin-dashboard-overview', params, selection.period]) !== undefined
  const overview = useAdminDashboardOverview(
    params,
    selection.period,
    !lightLiveViewEnabled && live && isToday,
    {
      enabled: !lightLiveViewEnabled || !isLiveStageOpen || !overviewHasLoaded,
      refetchOnWindowFocus: lightLiveViewEnabled ? false : undefined,
    },
  )
  const liveSeries = useAdminDashboardLiveSeries(params, isLiveStageOpen)
  const data = overview.data?.dashboard
  const rates = data ? dashboardOperationalRates(data.payments.global) : undefined
  const periodLabel = dashboardPeriodLabel(selection.period)
  const refresh = () => {
    void overview.refetch({ cancelRefetch: false })
  }
  const openLiveStage = () => {
    if (!lightLiveViewEnabled || !isToday) return
    liveStageOpenRef.current = true
    setLiveStageOpen(true)
  }
  const closeLiveStage = () => {
    if (!liveStageOpenRef.current) return
    liveStageOpenRef.current = false
    setLiveStageOpen(false)
    const url = new URL(window.location.href)
    if (url.searchParams.has('live')) {
      url.searchParams.delete('live')
      window.history.replaceState(
        window.history.state,
        '',
        `${url.pathname}${url.search}${url.hash}`,
      )
    }
    void overview.refetch({ cancelRefetch: false })
  }
  const computedAt = adminDashboardComputedAt(overview.data, overview.dataUpdatedAt)
  const updatedAt = computedAt
    ? new Date(computedAt).toLocaleTimeString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
      })
    : null

  return (
    <section className={page} data-testid="dashboard-screen">
      <div className={header}>
        <div className={titleBlock}>
          <h1 className={title}>Dashboard de gestão</h1>
          <p className={subtitle}>
            Volume de cobranças, taxas da plataforma e ocorrências. Recorte: {periodLabel}.
          </p>
        </div>
        <div className={styles.actions}>
          <span className={styles.description} role="status">
            {overview.isFetching
              ? 'Atualizando...'
              : overview.isError
                ? 'Atualização interrompida'
                : updatedAt
                  ? `Atualizado às ${updatedAt}`
                  : 'Aguardando dados'}
          </span>
          <Button variant="secondary" disabled={overview.isFetching} onClick={refresh}>
            <RefreshCcw size={16} />
            Atualizar
          </Button>
          {!lightLiveViewEnabled ? (
            <div className={styles.liveControl}>
              <Toggle
                checked={live && isToday}
                disabled={!isToday}
                onChange={setLive}
                ariaLabel="Atualização ao vivo"
              />
              <span className={styles.description}>Ao vivo</span>
            </div>
          ) : null}
        </div>
      </div>
      <AdminDashboardFilters
        selection={selection}
        currency={currency}
        onApply={(next) => {
          setSelection(next)
          if (next.period !== 'today') {
            setLive(false)
            if (liveStageOpenRef.current) closeLiveStage()
          }
        }}
      />
      <div className={styles.scope}>
        <strong>
          {selection.user
            ? `Usuário: ${selection.user.name || selection.user.email}`
            : 'Todos os usuários'}
        </strong>
        {selection.user?.name ? <span>{selection.user.email}</span> : null}
        <span>· {overview.data ? dashboardRangeLabel(overview.data.range) : periodLabel}</span>
        <span>· Valores apresentados em {currency}</span>
      </div>
      <p className={styles.description}>
        {lightLiveViewEnabled
          ? isToday
            ? 'Abra o gráfico em tela cheia para acompanhar as cobranças de hoje ao vivo.'
            : 'Selecione Hoje para acompanhar ao vivo em tela cheia.'
          : isToday
            ? live
              ? 'Ao vivo: consulta a cada 30 segundos enquanto esta aba estiver visível. Em caso de erro, use Atualizar para retomar.'
              : 'Ative Ao vivo para acompanhar as cobranças de hoje nesta tela.'
            : 'Selecione Hoje para ativar o acompanhamento ao vivo.'}
      </p>
      {overview.fetchStatus === 'paused' ? (
        <Banner tone="warning" title="Sem conexão">
          A atualização está pausada. Os valores exibidos podem estar desatualizados.
        </Banner>
      ) : null}
      {overview.isError && data ? (
        <Banner
          tone="danger"
          title="Não foi possível atualizar o dashboard"
          action={
            <Button variant="text" disabled={overview.isFetching} onClick={refresh}>
              Tentar novamente
            </Button>
          }
        >
          Os valores abaixo são da última atualização bem-sucedida e podem estar desatualizados.
        </Banner>
      ) : null}
      {overview.isPending ? (
        <div className={styles.stack} aria-label="Carregando indicadores">
          <Skeleton height={350} />
          <div className={styles.grid}>
            {[0, 1, 2].map((key) => (
              <Skeleton key={key} height={180} />
            ))}
          </div>
        </div>
      ) : data && overview.data ? (
        <>
          <AdminSalesChart
            data={overview.data.series}
            currency={currency}
            periodLabel={periodLabel}
            isLoading={false}
            showComparison={!isToday}
            live={
              lightLiveViewEnabled && isToday
                ? {
                    open: isLiveStageOpen,
                    series: liveSeries.data,
                    dataUpdatedAt: liveSeries.dataUpdatedAt,
                    isError: liveSeries.isError,
                    onOpen: openLiveStage,
                    onClose: closeLiveStage,
                    onRetry: () => void liveSeries.refetch(),
                  }
                : undefined
            }
          />
          {data.payments.totalSales.some((row) => (row.unconvertedCount ?? 0) > 0) ? (
            <Banner tone="warning" title="Volume parcial">
              Há cobranças sem conversão disponível. Elas não entram no valor do gráfico.
            </Banner>
          ) : null}
          <section className={styles.stack} aria-label="Volume e cobranças">
            <h2 className={panelTitle}>Volume e cobranças</h2>
            <div className={styles.grid}>
              <MetricCard
                label="Volume de cobranças pagas"
                rows={data.payments.totalSales}
                currency={currency}
                description="Valor bruto das cobranças criadas no período que estão pagas atualmente. Antes das taxas."
              />
              <MetricCard
                label="Cobranças com afiliado"
                rows={data.payments.affiliateSales}
                currency={currency}
                description="Volume das cobranças pagas com afiliado. Não representa a comissão do afiliado."
              />
              <MetricCard
                label="Cobranças com coprodutor"
                rows={data.payments.coProducerSales}
                currency={currency}
                description="Volume das cobranças pagas com coprodutor. Não representa a parcela do coprodutor."
              />
            </div>
            <div className={styles.scope}>
              Cobranças pagas: {data.payments.global.paidCount.toLocaleString('pt-BR')} · Pendentes:{' '}
              {data.payments.global.pendingCount.toLocaleString('pt-BR')} · Falhas:{' '}
              {data.payments.global.failedCount.toLocaleString('pt-BR')}
            </div>
          </section>
          <section className={styles.stack} aria-label="Taxas da plataforma">
            <h2 className={panelTitle}>Taxas da plataforma</h2>
            <div className={styles.grid}>
              <MetricCard
                label="Taxas VendePay sobre vendas"
                rows={data.settlement.saleFee}
                currency={currency}
                countLabel="lançamentos"
                description="Valor das tarifas de venda registradas no período. Não representa lucro líquido."
              />
              <MetricCard
                label="Valores de conversão de moeda"
                rows={data.settlement.currencyConversionFee}
                currency={currency}
                countLabel="lançamentos"
                description="Conversão cobrada do comprador ou deduzida da venda, conforme a regra registrada. Inclui custos e margem; não representa a tarifa exata do provedor."
              />
            </div>
          </section>
          <section className={styles.stack} aria-label="Estornos e chargebacks">
            <h2 className={panelTitle}>Estornos e chargebacks</h2>
            <div className={styles.grid}>
              <MetricCard
                label="Chargebacks"
                rows={data.payments.chargebacks}
                currency={currency}
                description="Valor contestado das cobranças criadas no período que estão em chargeback."
                rate={rates?.chargebacks}
                totalCount={data.payments.global.chargedBackCount}
              />
              <MetricCard
                label="Reembolsos totais"
                rows={data.payments.refunds}
                currency={currency}
                description="Inclui os reembolsos de prevenção de chargeback detalhados ao lado."
                rate={rates?.refunds}
                totalCount={data.payments.global.refundedCount}
              />
              <MetricCard
                label="Prevenções de chargeback"
                rows={data.payments.globalChargebackRefunds}
                currency={currency}
                description="Parte dos reembolsos totais identificada pela proteção contra chargeback. Não some este valor novamente."
              />
            </div>
            <p className={styles.description}>
              Percentuais operacionais por quantidade, sobre {rates?.base.toLocaleString('pt-BR')}{' '}
              cobranças atualmente pagas, reembolsadas ou em chargeback, criadas no período. Incluem
              todas as moedas, mesmo operações sem conversão disponível. Não equivalem às taxas por
              data de pagamento do Seller ou aos índices dos adquirentes.
            </p>
          </section>
          <Card padding="none" className={tableCard}>
            <div className={cardHeader}>
              <h2 className={panelTitle}>Meios de pagamento</h2>
            </div>
            <div className={tableViewport}>
              <table className={table}>
                <thead>
                  <tr>
                    <th className={th}>Método</th>
                    <th className={th}>Moeda</th>
                    <th className={th}>Cobranças pagas</th>
                    <th className={th}>Volume</th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.paymentMethods.map((row) => (
                    <tr key={`${row.currency}-${row.paymentMethod}`}>
                      <td className={td}>{paymentMethodLabel(row.paymentMethod)}</td>
                      <td className={td}>{row.currency}</td>
                      <td className={td}>{row.count.toLocaleString('pt-BR')}</td>
                      <td className={td}>
                        <MoneyValue amount={row.amount.amount} currency={row.currency} />
                      </td>
                    </tr>
                  ))}
                  {!data.payments.paymentMethods.length ? (
                    <tr>
                      <td className={td} colSpan={4}>
                        Sem cobranças pagas no período.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </Card>
          <Card padding="none" className={tableCard}>
            <div className={cardHeader}>
              <h2 className={panelTitle}>Recebíveis por moeda</h2>
            </div>
            <div className={tableViewport}>
              <table className={table}>
                <thead>
                  <tr>
                    <th className={th}>Moeda</th>
                    <th className={th}>Recebíveis</th>
                    <th className={th}>Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.settlement.receivablesByCurrency ?? []).map((row) => (
                    <tr key={row.currency}>
                      <td className={td}>{row.currency}</td>
                      <td className={td}>{row.count.toLocaleString('pt-BR')}</td>
                      <td className={td}>
                        <MoneyValue amount={row.amount.amount} currency={row.currency} />
                      </td>
                    </tr>
                  ))}
                  {!(data.settlement.receivablesByCurrency ?? []).length ? (
                    <tr>
                      <td className={td} colSpan={3}>
                        Sem recebíveis por moeda.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </Card>
          <p className={styles.description}>
            Cobranças seguem a data de criação e o estado atual; taxas e recebíveis seguem a data
            dos registros financeiros. Valores convertidos usam a cotação registrada na operação.
          </p>
        </>
      ) : (
        <ErrorState
          title="Não foi possível atualizar o dashboard"
          description="Nenhum valor foi carregado. Tente novamente."
          onRetry={refresh}
        />
      )}
    </section>
  )
}
